import React from "react";
import { Link, useParams, useNavigate, Routes, Route } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

/* -----------------------------------------
   Adatszerkezet: év  →  [{ slug, title, file }]
----------------------------------------- */
const reports = {
  2024: [
    { slug: "beszamolo", title: "Beszámoló", file: "2024.pdf" },
    { slug: "kiegeszito", title: "Kiegészítő melléklet", file: "2024_kiegeszito.pdf" },
    { slug: "kozhkulcs", title: "Közhasznúsági melléklet", file: "2024_kozhasznu.pdf" },
  ],
  2023: [
    { slug: "beszamolo", title: "Beszámoló", file: "2023.pdf" },
    { slug: "kiegeszito", title: "Kiegészítő melléklet", file: "2023_kiegeszito.pdf" },
    { slug: "kozhkulcs", title: "Közhasznúsági melléklet", file: "2023_kozhasznu.pdf" },
  ],
  2022: [
    { slug: "beszamolo", title: "Beszámoló", file: "2022.pdf" },
    { slug: "kiegeszito", title: "Kiegészítő melléklet", file: "2022_kiegeszito.pdf" },
    { slug: "kozhkulcs", title: "Közhasznúsági melléklet", file: "2022_kozhasznu.pdf" },
  ],
  2021: [
    { slug: "beszamolo", title: "Beszámoló", file: "2021.pdf" },
    { slug: "kiegeszito", title: "Kiegészítő melléklet", file: "2021_kiegeszito.pdf" },
    { slug: "kozhkulcs", title: "Közhasznúsági melléklet", file: "2021_kozhasznu.pdf" },
  ],
  2020: [
    { slug: "beszamolo", title: "Beszámoló", file: "2020.pdf" },
    { slug: "kiegeszito", title: "Kiegészítő melléklet", file: "2020_kiegeszito.pdf" },
    { slug: "kozhkulcs", title: "Közhasznúsági melléklet", file: "2020_kozhasznu.pdf" },
  ],
  2019: [
    { slug: "beszamolo", title: "Beszámoló", file: "2019.pdf" },
    { slug: "egyszerusitett", title: "Egyszerűsített éves beszámoló", file: "2019_egyszerusitett.pdf" },
    { slug: "kozhkulcs", title: "Közhasznúsági melléklet", file: "2019_kozhasznu.pdf" },
    { slug: "jovahagyo", title: "Jóváhagyó határozat", file: "2019_jovahagyo_hatarozat.pdf" },
    { slug: "kiegeszito", title: "Kiegészítő melléklet", file: "2019_kiegeszito.pdf" },
  ],
  2018: [
    { slug: "beszamolo", title: "Beszámoló", file: "2018.pdf" },
  ],
  2017: [
    { slug: "beszamolo", title: "Beszámoló", file: "2017.pdf" },
  ],
};
const years = Object.keys(reports).sort((a, b) => b - a);

// ----------------------------------------------------------------
// LISTA – /reports
// ----------------------------------------------------------------
export function ReportsList() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-16">
      <h1 className="mb-10 text-center text-3xl font-bold">Éves beszámolók</h1>
      <div className="grid gap-6 sm:grid-cols-2">
        {years.map((y) => (
            <Link
              key={y}
              to={`/reports/${y}`}
              className="flex h-32 items-center justify-center rounded-xl border border-gray-200 bg-white text-lg font-semibold text-primary shadow-sm hover:shadow-md"
            >
              Éves beszámoló {y}
            </Link>
        ))}
      </div>
    </main>
  );
}

// ----------------------------------------------------------------
// ÉV DOKUMENTUM‑LISTA – /reports/:year
// ----------------------------------------------------------------
export function ReportsYear() {
  const { year } = useParams();
  const nav = useNavigate();
  const docs = reports[year] ?? [];

  return (
    <main className="mx-auto max-w-4xl px-4 py-12">
      <button
        onClick={() => nav(-1)}
        className="mb-6 inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Vissza
      </button>

      <h1 className="mb-8 text-center text-3xl font-bold">{year}. évi dokumentumok</h1>

      {docs.length === 0 ? (
        <p className="text-center text-gray-500">Ehhez az évhez jelenleg nincs feltöltött fájl.</p>
      ) : (
        <ul className="space-y-4">
          {docs.map((d) => (
            <li key={d.slug}>
              <Link
                to={`/reports/${year}/${d.slug}`}
                className="flex items-center gap-3 rounded-md border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md"
              >
                <DocumentTextIcon className="h-6 w-6 text-primary" />
                <span className="font-medium text-gray-800">{d.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

// ----------------------------------------------------------------
// PDF VIEWER – /reports/:year/:slug
// ----------------------------------------------------------------
export function ReportViewer() {
  const { year, slug } = useParams();
  const nav = useNavigate();
  const doc = (reports[year] ?? []).find((d) => d.slug === slug);

  if (!doc) return <p className="p-10 text-center">Dokumentum nem található.</p>;

  const pdfSrc = `/reports/${doc.file}`;

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <button
        onClick={() => nav(-1)}
        className="mb-6 inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Vissza
      </button>

      <h1 className="mb-4 text-center text-3xl font-bold">{doc.title}</h1>

      <div className="mb-4 text-right">
        <a
          href={pdfSrc}
          download
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <ArrowDownTrayIcon className="h-5 w-5" /> Letöltés
        </a>
      </div>

      <iframe
        src={pdfSrc + "#toolbar=0"}
        title={doc.title}
        className="h-[80vh] w-full rounded-md border"
      />

      <p className="mt-4 text-center text-sm text-gray-500">
        Ha nem töltődne be a dokumentum, {" "}
        <a href={pdfSrc} className="text-primary underline">kattintson ide!</a>
      </p>
    </main>
  );
}

// ----------------------------------------------------------------
// Router helper komponens – 3 szintű nested route (év → slug)
// ----------------------------------------------------------------
export default function ReportsRoutes() {
  return (
    <Routes>
      <Route index element={<ReportsList />} />

      <Route path=":year">
        <Route index element={<ReportsYear />} />
        <Route path=":slug" element={<ReportViewer />} />
      </Route>
    </Routes>
  );
}
